/**
 * Critical Illness utility functions for configuring dependent benefit fields
 */

import { NotDisclosed, NotOffered } from "@platform/openapi-types";

import { BenefitDefinition } from "../../benefits/benefit.types";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import { buildTypeAmountWithVariantsBenefit } from "../shared/definitions/builders";

/**
 * Configuration for building employee benefit fields specific to Critical Illness
 */
export interface EmployeeBenefitFieldsConfig {
  /** Enum values for benefit description type dropdown */
  benefitDescriptionTypeEnumValues: string[];
  /** Enum values for benefit maximum type dropdown */
  benefitMaximumTypeEnumValues: string[];
  /** Enum values for guarantee issue type dropdown */
  guaranteeIssueTypeEnumValues: string[];
  /** Whether the employee benefit description type field is required for coverage completion */
  benefitDescriptionTypeRequired?: boolean;
  /** Whether the employee benefit maximum type field is required for coverage completion */
  benefitMaximumTypeRequired?: boolean;
  /** Whether the employee guarantee issue type field is required for coverage completion */
  guaranteeIssueTypeRequired?: boolean;
}

/**
 * Builds all employee benefit field definitions for Critical Illness
 *
 * Creates field definitions for:
 * - Benefit Description (Type + Amount with cellVariants + Minimum)
 * - Benefit Maximum (Type + DollarAmount + Percent with conditional disabling)
 * - Guarantee Issue (Type + Amount with cellVariants)
 *
 * This pattern is specific to Critical Illness where:
 * - BenefitDescription Amount uses cellVariants for percentage/multipleOptions
 * - BenefitMaximum has both DollarAmount and Percent fields with mutual exclusion
 * - GuaranteeIssue Amount uses cellVariants for percentage/flatAmount
 *
 * @param config - Configuration with enum values for the type dropdowns
 * @returns Object with all employee benefit field definitions ready for spreading
 *
 * @example
 * export const benefits: BenefitDefinitions = {
 *   ...buildEmployeeBenefitFields({
 *     benefitDescriptionTypeEnumValues: Object.values(CriticalIllnessEmployeeBenefitDescriptionTypeEnum),
 *     benefitMaximumTypeEnumValues: Object.values(CriticalIllnessEmployeeMaximumTypeEnum),
 *     guaranteeIssueTypeEnumValues: Object.values(RfpCriticalIllnessEmployeeGuaranteeIssueTypeEnum),
 *   }),
 * };
 */
export function buildEmployeeBenefitFields(
  config: EmployeeBenefitFieldsConfig,
): Record<string, BenefitDefinition> {
  const {
    benefitDescriptionTypeEnumValues,
    benefitMaximumTypeEnumValues,
    guaranteeIssueTypeEnumValues,
    benefitDescriptionTypeRequired,
    benefitMaximumTypeRequired,
    guaranteeIssueTypeRequired,
  } = config;

  return {
    // ========================================
    // BENEFIT DESCRIPTION - EMPLOYEE
    // ========================================
    employeeBenefitDescriptionType: {
      label: "Benefit Description - Employee",
      description: "Type of employee benefit description",
      cell: {
        ...sharedCellSpecs.enumDropdown,
        cellEditorParams: {
          ...sharedCellSpecs.enumDropdown.cellEditorParams,
          values: benefitDescriptionTypeEnumValues,
        },
      },
      networkDependent: false,
      refreshesCells: [
        "employeeBenefitDescriptionAmount",
        "employeeBenefitDescriptionMinimum",
      ],
      ...(benefitDescriptionTypeRequired && { isRequired: true }),
    },
    employeeBenefitDescriptionAmount: {
      label: "Amount",
      description: "Employee benefit amount",
      cell: sharedCellSpecs.dollarAmount,
      networkDependent: false,
      nested: true,
      defaultValue: 1,
      cellVariants: {
        dependsOn: "employeeBenefitDescriptionType",
        options: {
          percentage: sharedCellSpecs.percentageAmount,
          multipleOptions: {
            ...sharedCellSpecs.multipleDollarAmountsPopover,
            cellEditorParams: {
              ...sharedCellSpecs.multipleDollarAmountsPopover.cellEditorParams,
              maxEntries: 10,
            },
          },
          multipleDollarAmounts: {
            ...sharedCellSpecs.multipleDollarAmountsPopover,
            cellEditorParams: {
              ...sharedCellSpecs.multipleDollarAmountsPopover.cellEditorParams,
              maxEntries: 10,
            },
          },
          salaryMultiplier: sharedCellSpecs.salaryMultiplier,
          multipleSalaryMultipliers:
            sharedCellSpecs.multipleSalaryMultipliersPopover,
        },
        default: sharedCellSpecs.dollarAmount,
      },
      editableWhen: {
        dependsOn: "employeeBenefitDescriptionType",
        disabledValues: [NotDisclosed],
      },
    },
    employeeBenefitDescriptionMinimum: {
      label: "Minimum",
      description: "Minimum employee benefit amount",
      cell: sharedCellSpecs.dollarAmount,
      networkDependent: false,
      nested: true,
      editableWhen: {
        dependsOn: "employeeBenefitDescriptionType",
        disabledValues: [NotDisclosed],
      },
    },

    // ========================================
    // BENEFIT MAXIMUM - EMPLOYEE
    // ========================================
    employeeBenefitMaximumType: {
      label: "Benefit Maximum - Employee",
      description: "Type of employee benefit maximum",
      cell: {
        ...sharedCellSpecs.enumDropdown,
        cellEditorParams: {
          ...sharedCellSpecs.enumDropdown.cellEditorParams,
          values: benefitMaximumTypeEnumValues,
        },
      },
      networkDependent: false,
      refreshesCells: [
        "employeeBenefitMaximumDollarAmount",
        "employeeBenefitMaximumPercent",
      ],
      ...(benefitMaximumTypeRequired && { isRequired: true }),
    },
    employeeBenefitMaximumDollarAmount: {
      label: "Dollar Amount",
      description: "Maximum amount in dollars",
      cell: sharedCellSpecs.dollarAmount,
      networkDependent: false,
      nested: true,
      editableWhen: {
        dependsOn: "employeeBenefitMaximumType",
        disabledValues: ["percentageOfEeBenefit", NotDisclosed],
      },
    },
    employeeBenefitMaximumPercent: {
      label: "Percentage",
      description: "Maximum amount as percentage",
      cell: sharedCellSpecs.percentageAmount,
      networkDependent: false,
      nested: true,
      editableWhen: {
        dependsOn: "employeeBenefitMaximumType",
        disabledValues: ["dollarAmount", NotDisclosed],
      },
    },

    // ========================================
    // GUARANTEE ISSUE - EMPLOYEE
    // ========================================
    ...buildTypeAmountWithVariantsBenefit({
      prefix: "employeeGuaranteeIssue",
      typeLabel: "Guarantee Issue - Employee",
      typeCell: sharedCellSpecs.enumDropdown,
      typeEnumValues: guaranteeIssueTypeEnumValues,
      amountLabel: "Amount",
      typeDescription: "Type of employee guarantee issue",
      amountDescription: "Employee guarantee issue amount",
      cellSpecVariants: {
        percentageOfEeBenefit: sharedCellSpecs.percentageAmount,
        flatAmount: sharedCellSpecs.dollarAmount,
      },
      defaultCellSpec: sharedCellSpecs.dollarAmount,
      disabledValues: ["notIncluded", NotDisclosed],
      isRequired: guaranteeIssueTypeRequired,
    }),
  };
}

/**
 * Configuration for building an mpOnly condition-rider field (CD-2074/CD-2325):
 * a percentage-or-dollar-amount type+amount pair, gated to the side-by-side by
 * `mpOnly`/`mpConfigKey`.
 */
export interface MpOnlyConditionRiderConfig {
  /** Field-name prefix, e.g. "skinCancer" → skinCancerType/skinCancerAmount */
  prefix: string;
  /** Row label, e.g. "Skin Cancer" */
  typeLabel: string;
  /** Description for the type field */
  typeDescription: string;
  /** Description for the amount field */
  amountDescription: string;
  /** MP config key gating the type field's visibility */
  mpConfigKey: string;
}

/**
 * Builds a Critical Illness condition-rider field (Skin Cancer, Advanced
 * Parkinson's, ALS — CD-390/CD-2074): a type+amount pair whose product
 * decision (CD-2325) is a percentage or dollar amount alongside the usual
 * sentinels, gated `mpOnly` so it only renders in the side-by-side with no
 * business in Build. The popup editor and row-collapsing come from the
 * matching entry in `criticalIllnessTypedAmountFields`
 * (@platform/formatting-utils); the cellSpecVariants/defaultCellSpec here
 * just keep the raw cell specs correct for non-interactive consumers (e.g.
 * CSV export).
 */
export function buildMpOnlyConditionRider(
  config: MpOnlyConditionRiderConfig,
): Record<string, BenefitDefinition> {
  const { prefix, typeLabel, typeDescription, amountDescription, mpConfigKey } =
    config;

  const conditionRider = buildTypeAmountWithVariantsBenefit({
    prefix,
    typeLabel,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: [
      "percentage",
      "dollarAmount",
      "notIncluded",
      NotDisclosed,
      NotOffered,
    ],
    typeDescription,
    amountDescription,
    cellSpecVariants: {
      percentage: sharedCellSpecs.percentageAmount,
      dollarAmount: sharedCellSpecs.dollarAmount,
    },
    defaultCellSpec: sharedCellSpecs.dollarAmount,
    disabledValues: ["notIncluded", NotDisclosed, NotOffered],
  });

  return {
    [`${prefix}Type`]: {
      ...conditionRider[`${prefix}Type`],
      mpOnly: true,
      mpConfigKey,
    },
    [`${prefix}Amount`]: {
      ...conditionRider[`${prefix}Amount`],
      mpOnly: true,
    },
  };
}

/**
 * Configures dependent benefit field groups with external enable/disable control
 *
 * Applies conditional editability to the Type fields of all field groups based on
 * a control field (typically a yes/no enum) that must be defined separately.
 *
 * This creates a two-tier dependency structure:
 * 1. Control field (e.g., "dependentBenefitsIncluded") enables/disables Type fields
 * 2. Type fields enable/disable their own Amount/Duration fields (already configured by builders)
 *
 * @param benefitFieldGroups - Array of benefit field group objects from builders
 *   (e.g., from buildTypeAmountBenefitPair, buildTypeMultipleAmountsBenefit, buildTypeAmountWithVariantsBenefit)
 * @param controlFieldKey - Key of the control field that gates these field groups (e.g., "dependentBenefitsIncluded")
 * @param disabledValues - Values of the control field that disable the Type fields (e.g., ["no"])
 * @returns Flattened benefit definitions object with all fields properly configured
 *
 * @example
 * export const benefits = {
 *   // Define control field separately
 *   dependentBenefitsIncluded: {
 *     label: "Dependent Benefits Included?",
 *     cell: sharedCellSpecs.enumDropdown,
 *     // ...
 *   },
 *
 *   // Configure dependent field groups
 *   ...configureDependentBenefitFields(
 *     [
 *       buildTypeMultipleAmountsBenefit({ prefix: "spouseBenefitDescription", ... }),
 *       buildTypeAmountWithVariantsBenefit({ prefix: "spouseGuaranteeIssue", ... }),
 *       buildTypeMultipleAmountsBenefit({ prefix: "childBenefitDescription", ... }),
 *     ],
 *     "dependentBenefitsIncluded", // Control field key
 *     ["no"], // Disable when control field is "no"
 *   ),
 * };
 */
export const configureDependentBenefitFields = (
  benefitFieldGroups: Record<string, BenefitDefinition>[],
  controlFieldKey: string,
  disabledValues: string[],
): Record<string, BenefitDefinition> => {
  const result: Record<string, BenefitDefinition> = {};

  // Process all field groups
  for (const fieldGroup of benefitFieldGroups) {
    for (const [key, definition] of Object.entries(fieldGroup)) {
      // Add editableWhen to Type fields only
      if (key.endsWith("Type")) {
        result[key] = {
          ...definition,
          editableWhen: {
            dependsOn: controlFieldKey,
            disabledValues,
            // Reset to null so dependents are empty by default: enabling the
            // group (Dependent Benefits Included? → Yes) leaves the Type fields
            // blank rather than pre-filling "Not Included". null still reads as
            // disabled to the nested Amount/Minimum fields' own editableWhen, so
            // they keep displaying "-" while the group is off.
            resetValue: null,
          },
        };
      } else {
        // Amount/Duration fields pass through unchanged
        // They already have their own editableWhen from the builder
        result[key] = definition;
      }
    }
  }

  return result;
};
